// ============================================
// VERIFICARPUBLICO.JS — Verificador publico de documentos oficiales: /verificar (v2, 2026-09-09)
// ============================================
// SEMI-PRIVADO: ya no hay liga con el codigo dentro. La pagina es un FORMULARIO (folio + codigo) y el
// codigo llega al cliente por correo, no impreso en el PDF. Sin login (fuera del UsuarioProvider),
// con limite de 10 intentos por minuto por IP en el backend.
// Respuestas: vigente -> razon social, RFC, banco(s), ULTIMOS 4 de cada CLABE, emision y vigente hasta.
//             cualquier otro caso (vencida, revocada, folio inexistente, codigo incorrecto) -> mismo
//             mensaje generico, sin ningun dato.
// Las ligas viejas /verificar/DOC-... siguen abriendo esta pagina con el mensaje generico.
// Antifraude: "Beep Mexico nunca cambia sus datos bancarios por correo".

import React, { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { ShieldCheck, ShieldAlert, Landmark, Search } from 'lucide-react';
import { API_BASE } from './config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)
const AZUL = '#265F97', GRIS = '#3D4248', GRIS_CLARO = '#5E666E', BORDE = '#D7D9DB';
const fmt = (v) => { if (!v) return '—'; const d = new Date(v); return isNaN(d) ? '—' : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Mexico_City' }); };

export default function VerificarPublico() {
  const { codigo: codigoUrl } = useParams();
  const esGarantia = /^GAR-/i.test(codigoUrl || '');
  const [folio, setFolio] = useState('');
  const [codigo, setCodigo] = useState('');
  const [cargando, setCargando] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Liga vieja con el codigo dentro: se muestra el generico y el formulario para escribirlo bien.
  const [legado] = useState(!!codigoUrl && !esGarantia);

  const consultar = async (e) => {
    if (e) e.preventDefault();
    if (!folio.trim() || !codigo.trim()) { setError('Escribe el folio y el código que te enviaron.'); return; }
    setCargando(true); setError(null); setData(null);
    try {
      const r = await fetch(`${API}/api/verificar`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ folio: folio.trim(), codigo: codigo.trim() }) });
      const j = await r.json().catch(() => ({}));
      if (r.status === 429) throw new Error(j.error || 'Demasiados intentos. Espera un minuto e inténtalo de nuevo.');
      if (!r.ok || !j.ok) throw new Error(j.error || 'No se pudo verificar el documento.');
      setData(j);
    } catch (e2) { setError(e2.message); }
    finally { setCargando(false); }
  };

  if (esGarantia) return <Navigate to={`/garantia/${codigoUrl}`} replace />;

  const wrap = { minHeight: '100vh', background: '#F3F5F8', fontFamily: "'Poppins', system-ui, -apple-system, Segoe UI, Roboto, sans-serif", color: GRIS, padding: '16px 12px 40px' };
  const card = { background: '#fff', borderRadius: 14, border: `1px solid ${BORDE}`, padding: 18, boxShadow: '0 4px 14px rgba(38,95,151,0.06)' };
  const cont = { maxWidth: 720, margin: '0 auto', display: 'grid', gap: 14 };
  const input = { width: '100%', boxSizing: 'border-box', border: `1px solid ${BORDE}`, borderRadius: 10, padding: '12px 14px', fontSize: 16, minHeight: 48, fontFamily: 'monospace', letterSpacing: 1, textTransform: 'uppercase', color: GRIS, background: '#fff' };
  const lbl = { fontSize: 12.5, fontWeight: 600, color: GRIS_CLARO, marginBottom: 6 };

  return (
    <div style={wrap}>
      <div style={cont}>
        <div style={{ background: 'linear-gradient(90deg,#0F3A6B,#2E7AC4)', color: '#fff', borderRadius: 14, padding: '18px 20px' }}>
          <img src="/Logo%20Horizontal%20blanco.png" alt="Beep México" style={{ height: 26, display: 'block', marginBottom: 10 }} />
          <div style={{ fontSize: 12, letterSpacing: 1.2, opacity: 0.85, fontWeight: 600 }}>VERIFICACIÓN DE DOCUMENTO OFICIAL</div>
          <div style={{ fontSize: 14, marginTop: 4, opacity: 0.9 }}>Comprueba que una carta de datos bancarios de Beep México es auténtica y sigue vigente.</div>
        </div>

        {legado && !data && (
          <div style={{ ...card, borderLeft: '6px solid #9A5B00', background: '#FFFCF5' }}>
            <div style={{ fontWeight: 700, color: '#9A5B00' }}>Esta liga ya no verifica por sí sola</div>
            <div style={{ fontSize: 13.5, marginTop: 5, lineHeight: 1.55 }}>Por seguridad, ahora hay que escribir el folio y el código que Beep México te envió por correo.</div>
          </div>
        )}

        <form style={card} onSubmit={consultar}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr', maxWidth: 460 }}>
            <div>
              <div style={lbl}>Folio del documento</div>
              <input style={input} value={folio} onChange={e => setFolio(e.target.value.toUpperCase())} placeholder="DOC-2026-0001" autoComplete="off" spellCheck={false} inputMode="text" aria-label="Folio del documento" />
            </div>
            <div>
              <div style={lbl}>Código de verificación</div>
              <input style={input} value={codigo} onChange={e => setCodigo(e.target.value.toUpperCase())} placeholder="XXXXXXXX" maxLength={12} autoComplete="off" spellCheck={false} aria-label="Código de verificación" />
            </div>
            <button type="submit" disabled={cargando}
              style={{ minHeight: 48, borderRadius: 10, border: 'none', background: AZUL, color: '#fff', fontWeight: 700, fontSize: 15, cursor: cargando ? 'default' : 'pointer', opacity: cargando ? 0.65 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit' }}>
              <Search size={17} /> {cargando ? 'Verificando…' : 'Verificar documento'}
            </button>
          </div>
          <div style={{ fontSize: 12.5, color: GRIS_CLARO, marginTop: 12, lineHeight: 1.55 }}>
            El folio viene impreso en la carta; el código llega por separado en el correo de Beep México.
          </div>
        </form>

        {error && (
          <div style={{ ...card, borderLeft: '6px solid #B42318' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <ShieldAlert size={26} color="#B42318" style={{ flex: 'none' }} />
              <div><div style={{ fontWeight: 700, color: '#B42318' }}>No se pudo verificar</div><div style={{ fontSize: 13.5, marginTop: 4 }}>{error}</div></div>
            </div>
          </div>
        )}

        {data && !data.vigente && (
          <div style={{ ...card, borderLeft: '6px solid #B42318' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <ShieldAlert size={32} color="#B42318" style={{ flex: 'none' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: 16.5, color: '#B42318' }}>{data.mensaje}</div>
                <div style={{ fontSize: 13.5, marginTop: 6, lineHeight: 1.55 }}>Revisa que el folio y el código estén escritos igual que en el correo. Si coinciden y aun así no verifica, <strong>no realices ningún pago</strong>: confirma directamente con Beep México.</div>
              </div>
            </div>
          </div>
        )}

        {data && data.vigente && (
          <>
            <div style={{ ...card, borderLeft: '6px solid #1E7A4C' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <ShieldCheck size={34} color="#1E7A4C" style={{ flex: 'none' }} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17, color: '#1E7A4C' }}>Documento auténtico y vigente</div>
                  <div style={{ fontSize: 14, marginTop: 4, lineHeight: 1.5 }}>{data.mensaje}</div>
                  <div style={{ fontSize: 12.5, color: GRIS_CLARO, marginTop: 6 }}>Folio {data.folio} · emitido el {fmt(data.emitido_at)} · <strong>vigente hasta el {fmt(data.expira_en)}</strong></div>
                </div>
              </div>
            </div>
            <div style={card}>
              <div style={{ fontSize: 12, fontWeight: 700, color: AZUL, letterSpacing: 0.6, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}><Landmark size={16} /> DATOS DEL EMISOR</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: GRIS }}>{data.emisor}</div>
              <div style={{ fontSize: 13, color: GRIS_CLARO, marginBottom: 12 }}>RFC {data.rfc}</div>
              {(data.cuentas || []).map((c, i) => (
                <div key={i} style={{ border: `1px solid ${BORDE}`, borderRadius: 12, padding: 12, marginBottom: 8, background: '#F8FAFC', display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontWeight: 700, color: GRIS }}>{c.banco}</span>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#DEEBF7', color: AZUL, fontWeight: 700 }}>{c.moneda}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 14, color: GRIS, marginLeft: 'auto' }}>CLABE ···· {c.clabe_ultimos4 || '····'}</span>
                </div>
              ))}
              <div style={{ fontSize: 12.5, color: GRIS_CLARO, marginTop: 8, lineHeight: 1.55 }}>
                Por seguridad solo mostramos los últimos 4 dígitos: compáralos con los de tu carta. Deben coincidir.
              </div>
            </div>
          </>
        )}

        <div style={{ ...card, background: '#FFFCF5', borderColor: '#F3D9A4' }}>
          <div style={{ fontSize: 13.5, color: '#9A5B00', lineHeight: 1.6 }}>
            <strong>Beep México nunca cambia sus datos bancarios por correo.</strong> Si recibes un aviso de cambio de cuenta, no pagues y confirma por teléfono con tu contacto de siempre.
          </div>
        </div>
      </div>
    </div>
  );
}
